import { useState, useRef, useEffect } from 'react';
import {
  X,
  Download,
  Music,
  Play,
  Pause,
  Volume2,
  VolumeX,
  SkipBack,
  SkipForward,
  Loader2,
  Shuffle,
  Repeat,
  Repeat1,
  ChevronDown,
  ChevronUp,
  ListMusic,
  Save,
  Rewind,
  FastForward,
} from 'lucide-react';
import { useAudio } from '@/context/AudioContext';
import { QueuePanel } from '@/components/audio/QueuePanel';
import { PlaylistManager } from '@/components/audio/PlaylistManager';
import { triggerDownload } from '@/utils/download';

interface AudioPlayerDropdownProps {
  onClose: () => void;
}

// Format time in mm:ss
function formatTime(seconds: number): string {
  if (!seconds || !isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

type View = 'player' | 'playlists';

export function AudioPlayerDropdown({ onClose }: AudioPlayerDropdownProps) {
  const {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    volume,
    playbackRate,
    isLoading,
    pause,
    resume,
    stop,
    seek,
    setVolume,
    setPlaybackRate,
    // Queue state
    queue,
    queueIndex,
    shuffleMode,
    repeatMode,
    // Queue actions
    playNext,
    playPrevious,
    toggleShuffle,
    cycleRepeatMode,
    clearQueue,
    hasNext,
    hasPrevious,
  } = useAudio();

  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  const [view, setView] = useState<View>('player');
  const [saveQueueTrigger, setSaveQueueTrigger] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);

  // Close speed menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowSpeedMenu(false);
    };
    if (showSpeedMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showSpeedMenu]);

  // Reset save trigger after playlist manager handles it
  useEffect(() => {
    if (saveQueueTrigger && view !== 'playlists') {
      setSaveQueueTrigger(false);
    }
  }, [saveQueueTrigger, view]);

  // If no track and no queue, show playlists view by default
  const showPlaylistsOnly = !currentTrack && queue.length === 0;

  // Auto-switch to playlists view when there's no content
  useEffect(() => {
    if (showPlaylistsOnly && view === 'player') {
      setView('playlists');
    }
  }, [showPlaylistsOnly, view]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    seek(percentage * duration);
  };

  const handleSkipBack10 = () => {
    seek(Math.max(0, currentTime - 10));
  };

  const handleSkipForward10 = () => {
    seek(Math.min(duration, currentTime + 10));
  };

  const handleDownload = () => {
    if (!currentTrack) return;
    triggerDownload(currentTrack.url, currentTrack.fileName || currentTrack.title || 'audio');
  };

  const handleStop = () => {
    stop();
    onClose();
  };

  const toggleMute = () => {
    if (volume > 0) {
      setVolume(0);
    } else {
      setVolume(1);
    }
  };

  const handleSaveAsPlaylist = () => {
    setSaveQueueTrigger(true);
    setView('playlists');
  };

  const getRepeatIcon = () => {
    if (repeatMode === 'one') {
      return <Repeat1 size={16} />;
    }
    return <Repeat size={16} />;
  };

  const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 2];

  const playbackIcon = isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-0.5" />;
  const repeatLabel = repeatMode === 'all' ? 'All' : 'One';
  const repeatTitle = `Repeat: ${repeatMode === 'off' ? 'Off' : repeatLabel}`;

  // Playlist manager view
  if (view === 'playlists') {
    return (
      <>
        <div className="fixed inset-0 z-[90]" onClick={onClose} />
        <div className="fixed left-1/2 -translate-x-1/2 top-[calc(var(--header-height)+var(--safe-area-top)+8px)] w-[calc(100%-2rem)] max-w-sm bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-[100] max-h-[70vh] flex flex-col">
          <PlaylistManager
            isOpen={true}
            onClose={() => setView('player')}
            onSaveCurrentQueue={saveQueueTrigger}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[90]" onClick={onClose} />

      {/* Dropdown - Fixed positioned and centered */}
      <div className="fixed left-1/2 -translate-x-1/2 top-[calc(var(--header-height)+var(--safe-area-top)+8px)] w-[calc(100%-2rem)] max-w-sm bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-[100] max-h-[80vh] flex flex-col">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-[#8e4585] to-[#63345e] p-4 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {currentTrack?.thumbnailUrl ? (
                <img
                  src={currentTrack.thumbnailUrl}
                  alt={currentTrack.title}
                  className="w-10 h-10 rounded-lg object-cover"
                />
              ) : (
                <div className="bg-white/20 p-2 rounded-lg shrink-0">
                  <Music size={20} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-sm truncate">
                  {currentTrack?.title || 'No Track'}
                </h3>
                {queue.length > 1 && (
                  <p className="text-white/70 text-xs">
                    {queueIndex + 1} of {queue.length}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1 shrink-0">
              {currentTrack && (
                <button
                  onClick={handleDownload}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                  title="Download"
                >
                  <Download size={16} />
                </button>
              )}
              <button
                onClick={handleStop}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                title="Stop & Close"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        {currentTrack && (
          <div className="px-4 pt-4 flex-shrink-0">
            <div
              ref={progressRef}
              className="h-2 bg-slate-200 rounded-full cursor-pointer group"
              onClick={handleProgressClick}
            >
              <div
                className="h-full bg-gradient-to-r from-[#8e4585] to-[#a855a8] rounded-full relative transition-all"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-[#8e4585] rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        )}

        {/* Main Controls */}
        {currentTrack && (
          <div className="px-4 py-3 flex items-center justify-center gap-1 flex-shrink-0">
            {/* Shuffle */}
            <button
              onClick={toggleShuffle}
              className={`p-2 rounded-lg transition-colors ${
                shuffleMode
                  ? 'text-[#8e4585] bg-[#f8e8f8]'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              }`}
              title={shuffleMode ? 'Shuffle On' : 'Shuffle Off'}
            >
              <Shuffle size={16} />
            </button>

            {/* Previous Track */}
            <button
              onClick={playPrevious}
              disabled={!hasPrevious}
              className="p-2 text-slate-500 hover:text-[#8e4585] hover:bg-[#f8e8f8] rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Previous"
            >
              <SkipBack size={18} />
            </button>

            {/* Skip back 10s */}
            <button
              onClick={handleSkipBack10}
              className="p-1.5 text-slate-400 hover:text-[#8e4585] hover:bg-[#f8e8f8] rounded-lg transition-colors"
              title="Back 10s"
            >
              <Rewind size={14} />
            </button>

            {/* Play/Pause */}
            <button
              onClick={isPlaying ? pause : resume}
              className="p-3 bg-gradient-to-r from-[#8e4585] to-[#63345e] text-white rounded-full hover:shadow-lg transition-all mx-1"
              disabled={isLoading}
            >
              {isLoading ? <Loader2 size={24} className="animate-spin" /> : playbackIcon}
            </button>

            {/* Skip forward 10s */}
            <button
              onClick={handleSkipForward10}
              className="p-1.5 text-slate-400 hover:text-[#8e4585] hover:bg-[#f8e8f8] rounded-lg transition-colors"
              title="Forward 10s"
            >
              <FastForward size={14} />
            </button>

            {/* Next Track */}
            <button
              onClick={playNext}
              disabled={!hasNext}
              className="p-2 text-slate-500 hover:text-[#8e4585] hover:bg-[#f8e8f8] rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Next"
            >
              <SkipForward size={18} />
            </button>

            {/* Repeat */}
            <button
              onClick={cycleRepeatMode}
              className={`p-2 rounded-lg transition-colors ${
                repeatMode !== 'off'
                  ? 'text-[#8e4585] bg-[#f8e8f8]'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              }`}
              title={repeatTitle}
            >
              {getRepeatIcon()}
            </button>
          </div>
        )}

        {/* Secondary controls */}
        <div className="px-4 pb-3 flex items-center justify-between flex-shrink-0 border-b border-slate-100">
          {/* Volume */}
          <div className="relative">
            <button
              onClick={toggleMute}
              onMouseEnter={() => setShowVolumeSlider(true)}
              className="p-2 text-slate-500 hover:text-[#8e4585] hover:bg-[#f8e8f8] rounded-lg transition-colors"
            >
              {volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>

            {showVolumeSlider && (
              <div
                className="absolute bottom-full left-0 mb-2 p-2 bg-white rounded-lg shadow-lg border border-slate-200"
                onMouseLeave={() => setShowVolumeSlider(false)}
              >
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={(e) => setVolume(parseFloat(e.target.value))}
                  className="w-20 h-1 accent-[#8e4585]"
                />
              </div>
            )}
          </div>

          {/* Save as Playlist */}
          {queue.length > 0 && (
            <button
              onClick={handleSaveAsPlaylist}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 hover:text-[#8e4585] hover:bg-[#f8e8f8] rounded-lg transition-colors"
            >
              <Save size={14} />
              Save Playlist
            </button>
          )}

          {/* Playback speed */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowSpeedMenu(!showSpeedMenu);
              }}
              className="px-2 py-1 text-xs font-medium text-slate-600 hover:text-[#8e4585] hover:bg-[#f8e8f8] rounded-lg transition-colors"
            >
              {playbackRate}x
            </button>

            {showSpeedMenu && (
              <div
                className="absolute bottom-full right-0 mb-2 p-1 bg-white rounded-lg shadow-lg border border-slate-200"
                onClick={(e) => e.stopPropagation()}
              >
                {speedOptions.map((speed) => (
                  <button
                    key={speed}
                    onClick={() => {
                      setPlaybackRate(speed);
                      setShowSpeedMenu(false);
                    }}
                    className={`block w-full px-3 py-1.5 text-xs text-left rounded hover:bg-[#f8e8f8] transition-colors ${
                      playbackRate === speed ? 'text-[#8e4585] font-semibold' : 'text-slate-600'
                    }`}
                  >
                    {speed}x
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Queue section */}
        <div className="flex-shrink-0">
          <button
            onClick={() => setShowQueue(!showQueue)}
            className="w-full flex items-center justify-between px-4 py-2 hover:bg-slate-50 transition-colors"
          >
            <span className="text-xs font-medium text-slate-600">
              Queue ({queue.length} track{queue.length !== 1 ? 's' : ''})
            </span>
            {showQueue ? (
              <ChevronUp size={16} className="text-slate-400" />
            ) : (
              <ChevronDown size={16} className="text-slate-400" />
            )}
          </button>

          {showQueue && (
            <div className="border-t border-slate-100">
              <QueuePanel maxHeight={180} showHeader={false} onClear={() => setShowQueue(false)} />
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-4 py-2 flex items-center justify-between border-t border-slate-100 flex-shrink-0">
          <button
            onClick={() => setView('playlists')}
            className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-slate-600 hover:text-[#8e4585] hover:bg-[#f8e8f8] rounded-lg transition-colors"
          >
            <ListMusic size={14} />
            Playlists
          </button>

          {queue.length > 0 && (
            <button
              onClick={() => {
                clearQueue();
              }}
              className="text-xs text-slate-400 hover:text-red-500 transition-colors"
            >
              Clear Queue
            </button>
          )}
        </div>
      </div>
    </>
  );
}
